import { useOutletContext } from 'react-router-dom'
import type { SiteContext } from '../components/MainLayout'
import HomeServiceCard from '../components/HomeServiceCard'
import { FloatingDecorations, Marquee, Reveal, StaggerGroup } from '../components/motion'
import PortfolioCard from '../components/PortfolioCard'
import PricingPreview from '../components/PricingPreview'
import { ButtonLink, CallToAction, SectionHeading } from '../components/ui'
import { routeFor } from '../config/routes'
import { siteConfig } from '../config/site'
import { usePageMeta } from '../hooks/usePageMeta'

const marqueeItems = {
  el: ['ΙΣΤΟΣΕΛΙΔΕΣ', 'WEB ΕΦΑΡΜΟΓΕΣ', 'AI VIDEO', 'AI PERSONAS', 'ΠΑΡΑΓΩΓΗ ΗΧΟΥ', 'MIXING', 'MASTERING', 'MULTIMEDIA', 'ΨΗΦΙΑΚΑ ΠΡΟΪΟΝΤΑ'],
  en: ['WEBSITES', 'WEB APPLICATIONS', 'AI VIDEO', 'AI PERSONAS', 'AUDIO PRODUCTION', 'MIXING', 'MASTERING', 'MULTIMEDIA', 'DIGITAL PRODUCTS'],
} as const

export default function HomePageV2() {
  const { language, copy } = useOutletContext<SiteContext>()
  const page = copy.home
  const roseluneVisit = language === 'el' ? 'Επισκεφθείτε τη Roselune Everly' : 'Visit Roselune Everly'
  const roseluneCaseStudy = language === 'el' ? 'Δείτε το case study' : 'View the case study'
  usePageMeta({ language, title: language === 'el' ? 'Ψηφιακές εμπειρίες που ξεχωρίζουν' : 'Digital experiences that stand out', description: page.intro })

  return (
    <>
      <section className="hero" id="top">
        <div className="hero__ambient" aria-hidden="true" />
        <FloatingDecorations />
        <div className="shell hero__grid">
          <Reveal className="hero__content">
            <p className="eyebrow hero__eyebrow">{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p className="hero__intro">{page.intro}</p>
            <div className="founder-badge"><span className="founder-badge__mark" aria-hidden="true">FM</span><span className="founder-badge__copy">{page.founderBadge.map((line) => <span key={line}>{line}</span>)}</span></div>
            <div className="button-row">
              <ButtonLink to={routeFor(language, 'portfolio')}>{copy.common.viewPortfolio}</ButtonLink>
              <ButtonLink to={routeFor(language, 'contact')} variant="outline">{copy.common.requestQuote}</ButtonLink>
            </div>
            <p className="availability"><span aria-hidden="true" />{copy.common.available}</p>
          </Reveal>
          <Reveal className="hero__visual-reveal" direction="mask" delay={100}>
            <figure className="hero-visual">
              <div className="hero-visual__frame"><img src={siteConfig.assets.hero} alt={copy.common.heroAlt} fetchPriority="high" width="1536" height="1024" /></div>
              <figcaption><span>FOTIS MYTIKAS</span><span>{siteConfig.descriptor}</span></figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <Marquee items={marqueeItems[language]} label={language === 'el' ? 'Υπηρεσίες' : 'Services'} />

      <section className="section services-preview">
        <div className="shell">
          <SectionHeading eyebrow={page.servicesEyebrow} title={page.servicesTitle} text={page.servicesIntro} />
          <StaggerGroup className="home-service-grid">{page.serviceCards.map((service) => <HomeServiceCard key={service.number} {...service} to={`${routeFor(language, 'services')}#${service.anchor}`} />)}</StaggerGroup>
        </div>
      </section>

      <section className="section portfolio-preview">
        <div className="shell">
          <SectionHeading eyebrow={page.portfolioEyebrow} title={page.portfolioTitle} text={page.portfolioIntro} />
          <StaggerGroup className="portfolio-grid portfolio-grid--selected">{siteConfig.portfolio.map((project, index) => <PortfolioCard key={project.id} language={language} project={project} compact index={index} />)}</StaggerGroup>
          <div className="section-link-row"><ButtonLink to={routeFor(language, 'portfolio')} variant="outline">{copy.common.viewPortfolio}</ButtonLink></div>
        </div>
      </section>

      <section className="section roselune-section">
        <div className="shell roselune-section__grid">
          <Reveal className="roselune-feature__visual-reveal" direction="mask">
            <figure className="roselune-feature__visual"><img src="/roselune.webp" width="941" height="1672" alt={page.roseluneAlt} loading="lazy" decoding="async" /><figcaption>ROSELUNE EVERLY / {page.roseluneEyebrow}</figcaption></figure>
          </Reveal>
          <Reveal className="roselune-feature__content" direction="right" delay={80}>
            <p className="eyebrow">{page.roseluneEyebrow}</p><h2>{page.roseluneTitle}</h2>
            <p className="large-copy">{page.roseluneText}</p><p>{page.roseluneStudio}</p>
            <ul className="roselune-skills" aria-label={language === 'el' ? 'Δημιουργικά πεδία' : 'Creative disciplines'}>{page.roseluneSkills.map((skill) => <li key={skill}>{skill}</li>)}</ul>
            <div className="roselune-feature__actions">
              <a className="button button--solid" href={siteConfig.links.roselune} target="_blank" rel="noreferrer"><span>{roseluneVisit}</span><span aria-hidden="true">↗</span></a>
              <ButtonLink to={`${routeFor(language, 'portfolio')}#roselune-persona`} variant="outline">{roseluneCaseStudy}</ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <PricingPreview language={language} />
      <CallToAction eyebrow="FOTIS MYTIKAS / DIGITAL SERVICES" title={page.ctaTitle} text={page.ctaText} button={copy.common.requestQuote} to={routeFor(language, 'contact')} />
    </>
  )
}
